import { DatelyObject, Numeric, Years } from "../../util";
import { Translations } from "../../logic";
import { BicycleBenefitKind, BicycleBenefitUsecase, UserDefinedRow } from "../../model";
import { UsecaseValidationMessage } from "../UsecaseValidationMessage";
import { BaseUsecaseLogic } from "../BaseUsecaseLogic";

/** Client-side logic for BicycleBenefitUsecase  */
export class BicycleBenefitUsecaseLogic extends BaseUsecaseLogic {

  private _calculatedTaxable: number;
  private _calculatedTaxfree: number | undefined;
  private _row: UserDefinedRow;
  private _date: DatelyObject;

  /** Returns calculated taxable value. */
  public get calculatedTaxable(): number {
    return this._calculatedTaxable;
  }

  /** Returns calculated taxfree value */
  public get calculatedTaxfree(): number | undefined {
    return this._calculatedTaxfree;
  }

  /**
   * Updates the usecase values based on user input without going to the server.
   * @param row Row to update.
   * @param date The reference date for which the logic is done.
   * Typically the salary date of a calculation and the typically, the information needed is just the year for which the yearly changing numbers are fetched, but may later have more extended features.
   */
  public updateUsecase(row: UserDefinedRow, date: DatelyObject): void {
    row = row ?? this._row;
    this._row = row;
    date = date ?? this._date;
    this._date = date;

    const data: BicycleBenefitUsecase = row.data ?? {};
    if (!data.kind || data.kind == BicycleBenefitKind.NoDeduction || data.kind == BicycleBenefitKind.Undefined) {
        data.kind = BicycleBenefitKind.NoDeduction;
        data.deduction = null;
    } else {
      data.deduction = (data.deduction ?? 0) < 0 ? null : data.deduction;
    }

    data.taxable = (data.taxable ?? 0) < 0 ? null : data.taxable;

    if (data.kind == BicycleBenefitKind.PeriodicalDeduction) {
      data.periodDivider =  data.periodDivider ?? 12;
      data.periodDivider = data.periodDivider < 0 ? 12 : data.periodDivider;
      if ( data.periodDivider  == 0) {
        data.kind = BicycleBenefitKind.SingleDeduction;
      }
    }
    else
    {
      data.periodDivider = null;
    }

    const limit = Years.getYearlyChangingNumbers(date)?.sideCosts?.taxFreeBicycleBenefit ?? 0;
    let total = Numeric.round((row.price ?? 0) * (row.count ?? 1), 2) ?? 0;
    if (total < 0) {
      total = 0;
    }

    let deduction = data.deduction ?? 0;
    if (deduction > total) {
      deduction = 0;
      data.deduction = null;
    }
    if (data.taxable && data.taxable > ( total - deduction) ) {
      data.taxable = null;
    }

    const calcTaxable = Numeric.round(Math.max(0, total - deduction - limit), 2) ?? 0;
    if (data.taxable && data.taxable <= calcTaxable) {
      data.taxable = null;
    }
    const calcTaxfree = Numeric.round(Math.max(0, total - deduction - (data.taxable ?? calcTaxable)), 2);

    this._calculatedTaxable = data.taxable ?? calcTaxable;
    this._calculatedTaxfree = calcTaxfree;
  }

  /**
   * Gets validation message for the usecase.
   * @param row Row to validate
   * @returns A message that is either an error or OK (default). Warnings, info etc. may later be supported.
   */
  public getUcValidation(row: UserDefinedRow): UsecaseValidationMessage {
    const data: BicycleBenefitUsecase = row?.data || {};
    let msg;
    switch (data.kind) {
      case BicycleBenefitKind.SingleDeduction:
        msg = `${Translations.get("SALAXY.VALIDATION.Usecases.bicycleBenefit.singleDeduction")} ${Numeric.formatPrice(data.deduction || 0)}`;
        break;
      case BicycleBenefitKind.PeriodicalDeduction:
        msg = `${Translations.get("SALAXY.VALIDATION.Usecases.bicycleBenefit.periodicalDeduction")} ${Numeric.formatPrice(data.deduction || 0)} ${Translations.get("SALAXY.VALIDATION.Usecases.bicycleBenefit.periodicalDeduction2")} ${data.periodDivider || 12} ${Translations.get("SALAXY.VALIDATION.Usecases.bicycleBenefit.periodicalDeduction3")}`;
        break;
      case BicycleBenefitKind.NoDeduction:
      default:
        msg = "SALAXY.VALIDATION.Usecases.bicycleBenefit.noDeduction";
        break;
    }
    return { type: "default", msg };
  }
}
